import { isUndefined } from "../../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import TrackString from '../../../analytics/TrackString';
import ChapterHubEvent from "../../../app/chapter-hub/event/ChapterHubEvent";
import InitializeChapterHubEvent from "../../../app/chapter-hub/event/InitializeChapterHubEvent";
import ChapterLibrary from "../../../app/chapters/model/ChapterLibrary";
import LoadingScreenEvent from "../../../app/loading/event/LoadingScreenEvent";
import ProgressModel from "../../../app/progress/model/ProgressModel";
import SaveGameEvent from "../../../app/savegame/event/SaveGameEvent";
import DialogAudioController from "../../../app/sound/controller/DialogAudioController";
import AbstractCommand from "../../../core/application/AbstractCommand";
import AssetLoadServiceEvent from "../../../core/assetloading/events/AssetLoadServiceEvent";
import SoundType from "../../../core/sound/util/SoundType";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import NGTVariableManager from "../../base/NGTVariableManager";
import NPCModel from "../NPCModel";
import ScenarioLibrary from "../ScenarioLibrary";
import ScenarioPlayerEvent from "../ScenarioPlayerEvent";
import ScenarioCommandEvent from "./ScenarioCommandEvent";
import ScenarioPlayerRegisterEvent from "./ScenarioPlayerRegisterEvent";

/**
* PlayScenarioCommand.js
* 
* Created on 2-okt-2013, 16:08:55
*
* @author Ivo Swartjes for Ranj
**/
export default class PlayScenarioCommand extends AbstractCommand {
    constructor(injector, storyFactory, languagePack, stageManager, playSessionModel, soundManager,
        scenarioLibrary, dispatcher, assetLoadService, assetLocator, revisionToolGateway, variableManager, assetLibrary, chapterLibrary, npcModel, dialogAudioController, progressModel) {

        super(dispatcher);

        this._currScenarioPlayer;
        this._scenarioToPlay;
        this._storyFactory = storyFactory;
        this._playSessionModel = playSessionModel;
        /** @type {ScenarioLibrary} */
        this._scenarioLibrary = scenarioLibrary;
        this._assetLocator = assetLocator;
        this._dispatcher = dispatcher;
        this._injector = injector;
        this._assetLoadService = assetLoadService;
        this._revisionToolGateway = revisionToolGateway;
        /** @type {NGTVariableManager} */
        this._variableManager = variableManager;
        this._assetLibrary = assetLibrary;
        this._languagePack = languagePack;
        this._stageManager = stageManager;
        this._soundManager = soundManager;
        /** @type {ChapterLibrary} */
        this._chapterLibrary = chapterLibrary;
        /** @type {NPCModel} */
        this._npcModel = npcModel;
        /** @type {DialogAudioController} */
        this._dialogAudioController = dialogAudioController;
        /** @type {ProgressModel} */
        this._progressModel = progressModel;

        this._onScenarioLoaded = this.onScenarioLoaded.bind(this);
        this._onScenarioEnd = this.onScenarioEnd.bind(this);
        this._onLoadingAnimationFinished = this.onLoadingAnimationFinished.bind(this);

        this._awaitLoadingAnimation = new Promise(resolve => {
            this._resolveLoadingAnimationPromise = resolve;
        });
    }

    execute(event) {
        // Decide which scenario to play
        if (isUndefined(event.params) || isUndefined(event.params.scenario)) {
            this._scenarioToPlay = this._scenarioLibrary.getDefaultScenario();
        } else {
            this._scenarioToPlay = event.params.scenario;
        }

        // Set the current scenario in the library
        this._scenarioLibrary.setCurrentScenario(this._scenarioToPlay);

        this._dispatcher.addEventListener(LoadingScreenEvent.LOADING_ANIMATION_FINISHED, this._onLoadingAnimationFinished);
        this.dispatch(LoadingScreenEvent.SHOW_REQUEST);

        // Load scenario assets
        this._dispatcher.addEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoaded);
        this._assetLoadService.loadGroups(this._scenarioToPlay.id);
        //also add to assetLocator (so DialogEditorGateway can find assets)
        this._assetLocator.addManifest(this._assetLoadService.getManifest(this._scenarioToPlay.id));

        const lazyLoadAssetManifest = this._assetLoadService.getManifest(this._scenarioToPlay.id + "_lazyLoad");
        this._assetLocator.addManifest(lazyLoadAssetManifest);
    }

    async onScenarioLoaded(event) {
        this._dispatcher.removeEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoaded);

        console.log(`[${PlayScenarioCommand.name}] Scenario loading finished.`);

        // Ensure that the loading animation has finished before proceeding.
        await this._awaitLoadingAnimation;
        this.dispatch(LoadingScreenEvent.HIDE_REQUEST);

        // Start a new scenario play session
        this._playSessionModel.startNewScenarioSession(this._scenarioToPlay.id);

        // Analytics: set scenario id as persistent value so all events during scenario are attributed accordingly
        // NB: value is unset when dialog closes/completes
        this.trackPersistentValue(TrackString.PERSISTENT_STORY_ID, this._scenarioToPlay.id);
        this.trackEvent(TrackString.EVENT_SCENARIO_START);

        // Create a new scenario player
        //	NB: scenarioPlayer is not a singleton
        this._currScenarioPlayer = this._injector.getValue('scenarioPlayer');

        // Create story
        this._scenarioToPlay.story = this._storyFactory.getStory(this._scenarioToPlay.storyID);

        // Reset NPCs.
        this._npcModel.clear();

        // Initialize scenario 
        // TODO: from story memento, if any; else start with clear variable manager
        this._variableManager.reset();
        this._currScenarioPlayer.init(this._scenarioToPlay, null);

        var sprEvent = new ScenarioPlayerRegisterEvent(ScenarioPlayerRegisterEvent.REGISTER, this._currScenarioPlayer);
        this._dispatcher.dispatch(sprEvent.type, sprEvent);

        // TODO: restore story variables (if any)

        // Initialize chapter hub.
        const initializeChapterHubEvent = new InitializeChapterHubEvent(InitializeChapterHubEvent.PERFORM, this._chapterLibrary.getCurrentChapter().id);
        this.dispatch(initializeChapterHubEvent.type, { data: initializeChapterHubEvent });

        // Start the scenario!
        this._startTime = Date.now();
        this._currScenarioPlayer.eventDispatcher.addEventListener(ScenarioPlayerEvent.SCENARIO_END, this._onScenarioEnd);
        this._currScenarioPlayer.play();

        this._soundManager.playSound(SoundType.TRANSITION_TO_NEW_SCENARIO);
    }

    onScenarioEnd(event) {
        this._dialogAudioController.stopAllAudioChannels();

        // NOTE: We persist progress in the ConversationResultScreenMediator aswell,
        // as users might already assume that the game has been saved there.
        // We also save here, as not all scenarios have a result screen.
        this._progressModel.markScenarioCompleted(this._scenarioToPlay.id);
        this.dispatch(SaveGameEvent.SAVE);

        var sprEvent = new ScenarioPlayerRegisterEvent(ScenarioPlayerRegisterEvent.DEREGISTER, this._currScenarioPlayer);
        this._dispatcher.dispatch(sprEvent.type, sprEvent);

        // Clean up the hub.
        this.dispatch(ChapterHubEvent.DESTROY);

        // Analytics: tracks scenario end
        var duration = Date.now() - this._startTime;
        this.trackEvent(TrackString.EVENT_SCENARIO_END, TrackString.createScenarioEndParams({ duration }));

        // Analytics: unset scenario id from persistent value 
        this.clearPersistentValue(TrackString.PERSISTENT_STORY_ID);

        this._dispatcher.removeEventListener(AssetLoadServiceEvent.LOAD_COMPLETE, this._onScenarioLoaded);
        this._currScenarioPlayer.eventDispatcher.removeEventListener(ScenarioPlayerEvent.SCENARIO_END, this._onScenarioEnd);
        this._currScenarioPlayer.stop();
        this._currScenarioPlayer = null;

        this._injector = null;
        this._scenarioToPlay = null;
        this._storyFactory = null;
        this._playSessionModel = null;
        this._revisionToolGateway = null;

        this._onScenarioLoaded = null;
        this._onScenarioEnd = null;

        // Play the next scenario.
        const nextScenario = this._scenarioLibrary.getNextScenario();
        if (nextScenario) {
            this.dispatch(ScenarioCommandEvent.PLAY_SCENARIO, { scenario: nextScenario });
        } else {
            // Last scenario completed.
            // Show the game outro sequence and track game end.
            this.trackEvent(TrackString.EVENT_GAME_END);
            this.openScreen(SkinKeyRegistry.GAME_OUTRO_SCREEN_SKIN);
        }
    }

    onLoadingAnimationFinished(evt) {
        this._dispatcher.removeEventListener(LoadingScreenEvent.LOADING_ANIMATION_FINISHED, this._onLoadingAnimationFinished);
        console.log(`[${PlayScenarioCommand.name}] Loading animation finished.`);

        this._resolveLoadingAnimationPromise();
    }
}